/**
 * PersonaSelector — pill-tab row for selecting the active Expert Chat persona.
 *
 * - Fetches personas via TanStack Query (5-min stale time).
 * - Persists selection to localStorage (`preferred_persona_slug`).
 * - Default: 'analyst'. Error state: silent (stays on current/default persona).
 * - Mobile: pills scroll horizontally.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { personaApi } from '../../services/personaApi';

const STORAGE_KEY = 'preferred_persona_slug';
const DEFAULT_SLUG = 'analyst';

interface PersonaSelectorProps {
  selectedSlug: string;
  onSelect: (slug: string) => void;
}

const PersonaSelector: React.FC<PersonaSelectorProps> = ({ selectedSlug, onSelect }) => {
  const { data: personas, isLoading } = useQuery({
    queryKey: ['personas'],
    queryFn: () => personaApi.listPersonas(),
    staleTime: 5 * 60 * 1000,
  });

  const handleSelect = (slug: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, slug);
    } catch { /* ignore */ }
    onSelect(slug);
  };

  if (isLoading) {
    return (
      <div className="flex gap-2 px-3 py-2 overflow-x-auto">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-8 w-24 rounded-full bg-slate-700 animate-pulse flex-shrink-0"
          />
        ))}
      </div>
    );
  }

  if (!personas || personas.length === 0) return null;

  return (
    <div className="flex gap-2 px-3 py-2 overflow-x-auto flex-nowrap sm:flex-wrap border-b border-slate-700">
      {personas.map((persona) => {
        const isSelected = persona.slug === selectedSlug;
        return (
          <button
            key={persona.slug}
            onClick={() => handleSelect(persona.slug)}
            aria-pressed={isSelected}
            className={`
              flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors
              ${isSelected
                ? 'bg-teal-600 border-teal-500 text-white'
                : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'}
            `}
            title={persona.domains.join(', ')}
          >
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-600 text-xs font-bold flex-shrink-0">
              {persona.name.charAt(0)}
            </span>
            {persona.name}
            <span className="text-xs opacity-70">{persona.displayTitle}</span>
          </button>
        );
      })}
    </div>
  );
};

/** Read the stored persona slug from localStorage. Defaults to 'analyst'. */
export function getStoredPersonaSlug(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? DEFAULT_SLUG;
  } catch {
    return DEFAULT_SLUG;
  }
}

export default PersonaSelector;
